/**
 * TradeTab — extracted from /app/frontend/src/pages/PalGame.jsx
 * as part of the per-tab split. Behaviour identical to the
 * pre-split monolith; only the file boundary changed.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PayoutFlash } from "@/components/PayoutFlash";
import { AchievementUnlocked } from "@/components/AchievementUnlocked";
import { MasterTrainerCinematic } from "@/components/MasterTrainerCinematic";
import { useMasterTrainers } from "@/hooks/useMasterTrainers";
import {
  Swords, Sparkles, Zap, Compass, Trophy, Layers, Target, Castle,
  ArrowLeftRight, Map, Crosshair, Heart, ChevronLeft, BookOpen,
} from "lucide-react";
import { PalPicker, ActionWithPal, firePalgamePayout, firePalgameDefeat } from "./_shared";

export function TradeTab() {
  const toast = useToast();
  const { refreshProfile } = useAuth();
  const [pals, setPals] = useState([]);
  const [players, setPlayers] = useState([]);
  const [trades, setTrades] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = async () => {
    try {
      const [boxRes, playersRes, inboxRes] = await Promise.all([
        api.get("/palgame/box"),
        api.get("/palgame/trade/players"),
        api.get("/palgame/trade/inbox"),
      ]);
      setPals(boxRes.data.pals || []);
      setPlayers(playersRes.data.players || []);
      setTrades(inboxRes.data.trades || []);
    } catch (e) {
      toast.error(apiError(e, "Failed to load trade center"));
      setPals([]); setPlayers([]); setTrades([]);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const respond = async (tradeId, action) => {
    try {
      await api.post(`/palgame/trade/${tradeId}/respond?action=${action}`);
      toast.success(action === "accept" ? "Trade accepted" : "Trade declined");
      refreshProfile();
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const cancel = async (tradeId) => {
    try {
      await api.post(`/palgame/trade/${tradeId}/cancel`);
      toast.success("Trade cancelled");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="trade-tab">
      <div className="flex items-center justify-between">
        <h3 className="label">Trade Center</h3>
        <button onClick={() => setShowCreate(true)} className="btn btn-sm" data-testid="trade-new-btn">+ New Trade</button>
      </div>

      <p className="text-xs text-[var(--muted)] -mt-4">
        Trade pals or skulls with other players. 5% tax on currency. Trades expire after 10 minutes.
      </p>

      {trades.length === 0 ? (
        <div className="glass-card p-8 text-center text-[var(--muted)] text-sm">
          No trades yet. Click "+ New Trade" to start one.
        </div>
      ) : (
        <div className="space-y-3">
          {trades.map((t) => (
            <TradeRow key={t.id} t={t} onRespond={respond} onCancel={cancel} />
          ))}
        </div>
      )}

      {showCreate && (
        <NewTradeModal
          pals={pals}
          players={players}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refresh(); }}
        />
      )}
    </div>
  );
}

function TradeRow({ t, onRespond, onCancel }) {
  const isPending = t.status === "pending";
  const isIncoming = t.direction === "incoming";
  const otherName = isIncoming ? t.sender_name : t.receiver_name;
  const statusColor = {
    pending: "var(--teal)",
    accepted: "#66BB6A",
    declined: "#FF6B35",
    expired: "var(--muted)",
    cancelled: "var(--muted)",
  }[t.status] || "var(--muted)";

  return (
    <div className="glass-card p-4 flex flex-col sm:flex-row gap-4" data-testid={`trade-row-${t.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs uppercase tracking-wider text-[var(--muted)]">
            {isIncoming ? `From ${otherName}` : `To ${otherName}`}
          </span>
          <span
            className="text-[10px] uppercase px-2 py-0.5 rounded"
            style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}66` }}
          >
            {t.status}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-[10px] text-[var(--muted)] uppercase">{isIncoming ? "Offering" : "You offer"}</div>
            {t.offer_pal && <div className="flex items-center gap-2 mt-1"><PalPortrait name={t.offer_pal} size="w-8 h-8" /><span>{t.offer_pal}</span></div>}
            {t.offer_currency > 0 && <div className="text-xs mt-1 inline-flex items-center gap-1"><ChilletCoinIcon size={12}/> {t.offer_currency.toLocaleString()}</div>}
          </div>
          <div>
            <div className="text-[10px] text-[var(--muted)] uppercase">{isIncoming ? "Wants" : "You want"}</div>
            {t.request_pal && <div className="flex items-center gap-2 mt-1"><PalPortrait name={t.request_pal} size="w-8 h-8" /><span>{t.request_pal}</span></div>}
            {t.request_currency > 0 && <div className="text-xs mt-1 inline-flex items-center gap-1"><ChilletCoinIcon size={12}/> {t.request_currency.toLocaleString()}</div>}
          </div>
        </div>
      </div>
      {isPending && (
        <div className="flex gap-2 sm:flex-col">
          {isIncoming ? (
            <>
              <button onClick={() => onRespond(t.id, "accept")} className="btn btn-sm" data-testid={`trade-accept-${t.id}`}>Accept</button>
              <button onClick={() => onRespond(t.id, "decline")} className="btn btn-sm btn-secondary" data-testid={`trade-decline-${t.id}`}>Decline</button>
            </>
          ) : (
            <button onClick={() => onCancel(t.id)} className="btn btn-sm btn-secondary" data-testid={`trade-cancel-${t.id}`}>Cancel</button>
          )}
        </div>
      )}
    </div>
  );
}

function NewTradeModal({ pals, players, onClose, onCreated }) {
  const toast = useToast();
  const [receiverId, setReceiverId] = useState("");
  const [offerPal, setOfferPal] = useState("");
  const [offerCurrency, setOfferCurrency] = useState(0);
  const [requestPal, setRequestPal] = useState("");
  const [requestCurrency, setRequestCurrency] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!receiverId) { toast.error("Pick a player"); return; }
    if (!offerPal && Number(offerCurrency) <= 0) { toast.error("Offer something"); return; }
    if (!requestPal && Number(requestCurrency) <= 0) { toast.error("Request something"); return; }
    setBusy(true);
    try {
      await api.post("/palgame/trade/create", {
        receiver_user_id: receiverId,
        offer_pal: offerPal || null,
        offer_currency: Number(offerCurrency) || 0,
        request_pal: requestPal || null,
        request_currency: Number(requestCurrency) || 0,
      });
      toast.success("Trade sent!");
      onCreated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="new-trade-modal">
      <div onClick={(e) => e.stopPropagation()} className="glass-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl glow-teal mb-4" style={{ fontFamily: "'Cinzel Decorative', serif" }}>New Trade</h3>

        <label className="label">Trade with</label>
        <select value={receiverId} onChange={(e) => setReceiverId(e.target.value)} className="input mb-3 w-full" data-testid="trade-receiver-select">
          <option value="">— pick a player —</option>
          {players.map((p) => <option key={p.user_id} value={p.user_id}>{p.name || p.user_id}</option>)}
        </select>

        <label className="label">You offer</label>
        <select value={offerPal} onChange={(e) => setOfferPal(e.target.value)} className="input mb-2 w-full" data-testid="trade-offer-pal-select">
          <option value="">— no pal —</option>
          {pals.map((p) => <option key={p.pal_name} value={p.pal_name}>{p.pal_name} (Lv {p.level})</option>)}
        </select>
        <input type="number" min="0" value={offerCurrency} onChange={(e) => setOfferCurrency(e.target.value)} placeholder="Skulls (0 = none)" className="input mb-3 w-full" data-testid="trade-offer-currency-input" />

        <label className="label">You want</label>
        <input type="text" value={requestPal} onChange={(e) => setRequestPal(e.target.value)} placeholder="Pal name (or leave blank)" className="input mb-2 w-full" data-testid="trade-request-pal-input" />
        <input type="number" min="0" value={requestCurrency} onChange={(e) => setRequestCurrency(e.target.value)} placeholder="Skulls (0 = none)" className="input mb-4 w-full" data-testid="trade-request-currency-input" />

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn btn-sm btn-secondary" data-testid="trade-modal-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn btn-sm" data-testid="trade-modal-submit">{busy ? "Sending…" : "Send Trade"}</button>
        </div>
      </div>
    </div>
  );
}



// ============================================================================
// PVP TAB — challenge another player to a Pal battle (escrow wagers)
// ============================================================================

